import { type JwtPayload, STORAGE_KEYS } from '@shared/all/domains/trpc';
import {
  type DetailsCookiePayload,
  detailsCookieSchema,
  type EmailVerificationStatus
} from '@shared/all/domains/user';

/**
 * Parse a JWT token payload (used by mobile which reads JWT from AsyncStorage).
 */
export const parseJwt = (accessToken: string): JwtPayload | null => {
  const base64Url = accessToken.split('.')[1];
  const base64 = base64Url?.replace(/-/g, '+').replace(/_/g, '/');
  if (!base64) return null;
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(jsonPayload) as JwtPayload;
};

const clientCookieValues = (): string[] => {
  if (typeof document === 'undefined') return [];
  const prefix = `${STORAGE_KEYS.CLIENT_TOKEN}=`;
  return document.cookie
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part.startsWith(prefix))
    .map((part) => part.slice(prefix.length));
};

const payloadTime = (cookieValue: string): number =>
  new Date(readClientPayload(cookieValue)?.updatedAt ?? 0).getTime() || 0;

/**
 * Read the client cookie value from document.cookie.
 * Returns the raw cookie value (truthy if logged in, empty string if not).
 * Name kept as getAcccessToken for backward compatibility with existing callers.
 *
 * A browser can hold two: the real one on `Domain=factiii.com`, and a host-only
 * one minted between the @factiii/auth 0.12.2 bump and the domain fix (2-3 Jul
 * 2026). Nothing rewrites the host-only one, and the browser lists it first, so
 * reading the first match showed a signed-in user as signed out. So the
 * host-only copy is deleted here — the client cookie is not httpOnly, and a
 * delete without `Domain` touches only that copy — and of what is left, the
 * newest payload wins. The server drops its own host-only copies too
 * (apps/server/src/helpers/authCookieClear.ts).
 */
export const getAcccessToken = () => {
  let values = clientCookieValues();
  if (values.length > 1) {
    document.cookie = `${STORAGE_KEYS.CLIENT_TOKEN}=; Max-Age=0; Path=/`;
    values = clientCookieValues();
  }
  return values.reduce(
    (newest, value) =>
      payloadTime(value) > payloadTime(newest) ? value : newest,
    values[0] ?? ''
  );
};

/**
 * Extract the client cookie payload (userId + updatedAt) without verifying HMAC.
 * The server-side parseClientCookiePayload from @factiii/auth verifies the signature;
 * this client-side version only reads the JSON part for display purposes.
 */
export const readClientPayload = (
  cookieValue: string
): { userId: number; updatedAt: string } | null => {
  const dotIndex = cookieValue.indexOf('.');
  if (dotIndex === -1) return null;
  const data = cookieValue.slice(0, dotIndex);
  try {
    // base64url → standard base64 for atob
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as { userId: number; updatedAt: string };
  } catch {
    return null;
  }
};

// Returns actual ID for client (since cookies are synchronous) and 0 for mobile (since AsyncStorage is async)
export const getInitialUserId = (): number => {
  // Check if we're in a browser environment (client)
  if (typeof document !== 'undefined') {
    const cookieValue = getAcccessToken();
    if (!cookieValue) return 0;
    return readClientPayload(cookieValue)?.userId || 0;
  }

  // For mobile/React Native, return 0 since AsyncStorage is async
  return 0;
};

export const readFullClientPayload = (
  cookieValue: string
): DetailsCookiePayload | null => {
  const dotIndex = cookieValue.indexOf('.');
  if (dotIndex === -1) return null;
  const data = cookieValue.slice(0, dotIndex);
  try {
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    const parsed = JSON.parse(json);
    const result = detailsCookieSchema.safeParse(parsed);
    if (!result.success) return null;
    return result.data;
  } catch {
    return null;
  }
};

// No age limit. The payload's `updatedAt` is `User.updatedAt` — when the
// profile last changed, not when the cookie was written — so a 24-hour cut-off
// signed out everyone who had not edited their profile for a day, and
// auth.refresh could not cure it (it re-mints the same stamp). The details stay
// current without one: the auth middleware rewrites this cookie whenever
// `User.updatedAt` moves, and a dead session still ends on its first 401.
export const buildUserFromCookieValue = (cookieValue: string) => {
  if (!cookieValue) return null;
  const payload = readFullClientPayload(cookieValue);
  if (!payload) return null;
  return buildUserShape(payload);
};

export const getInitialUserFromCookie = () => {
  if (typeof document === 'undefined') return null;
  return buildUserFromCookieValue(getAcccessToken());
};

const buildUserShape = (payload: DetailsCookiePayload) => {
  return {
    isAuthenticated: true as const,
    id: payload.userId,
    username: payload.username,
    name: payload.name,
    email: payload.email,
    tag: payload.tag,
    avatarKey: payload.avatarKey,
    robohash: payload.robohash,
    hasPassword: payload.hasPassword,
    hasPasskey: payload.hasPasskey,
    twoFaEnabled: payload.twoFaEnabled,
    oauthProviders: payload.oauthProviders,
    oauthProvider: payload.oauthProvider,
    emailVerificationStatus:
      payload.emailVerificationStatus as EmailVerificationStatus,
    isPrivate: payload.isPrivate,
    isIncognito: payload.isIncognito,
    verificationRequestedAt: payload.verificationRequestedAt,
    deletionScheduledAt: payload.deletionScheduledAt,
    onboardingPath: payload.onboardingPath,
    bio: payload.bio ?? '',
    bannerKey: payload.bannerKey ?? '',
    tronsBalance: payload.tronsBalance
  };
};
